import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light.css';
import 'tippy.js/themes/light-border.css';
import 'tippy.js/themes/material.css';
import 'tippy.js/themes/translucent.css';

import { useEffect } from 'react';
import Draggable from 'react-draggable';
import { CODE_EDITOR, EDITOR_VIEW_NAME, STEP_POPPER } from '../core/constants';
import { getDomainCfg, store } from '../core/state';
import { getComponentData, getViewData, setComponentData } from '../core/views';
import { showSuccess } from '../features/successsteps';
import { CloseButton } from './buttons/base/CloseButton';
import { requestAceControl } from './MonacoEditorHelper';
import { setPopperHelper } from './PopperHelper';
import { isResponsiveView } from '../features/responsive';
import { isDarkMode } from './ThemeContext';

export const CLICK_EVENT = "clicked";
export const KEY_EVENT = "key";

export const hideStep = () : void => {
  const stepPopper = getComponentData(STEP_POPPER);
  stepPopper.show = false;
  setComponentData(STEP_POPPER, stepPopper);
}

export const requestNextStep = (eventName, data) : void => {
  const stepPopper = getComponentData(STEP_POPPER);
  if (!stepPopper.currentStep) {
    return;
  }

  const currentId = stepPopper.currentStep.id;
  const currentStep = stepPopper.currentStep;

  if (checkNextStepCondition(currentStep, eventName, data)) {
    nextStep(currentId)
  }
}

function checkNextStepCondition(currentStep, eventName, data) {
  return currentStep.event && currentStep.event === eventName && currentStep.data && currentStep.data === data;
}

export const nextStep = (finishedId : number) : void => {
  const stepPopper = getComponentData(STEP_POPPER);
  if (!stepPopper.currentStep) {
    return;
  }

  const currentId = stepPopper.currentStep.id;

  if (finishedId < currentId) {
    return;
  }

  hideStep();
  const state = getViewData(EDITOR_VIEW_NAME);
  const lastIndex = state.project.steps.length - 1;
  const lastStepId = state.project.steps[lastIndex].id;
  let nextIndex = finishedId;
  if (state.data?.projectFinished) {
    return;
  }

  if (nextIndex < state.project.steps.length) {
    const nextStepId = state.project.steps[nextIndex].id;
    showStep(nextStepId);
  } else {
    showSuccess();
  }
}

export const showStep = (stepId? : number) : void => {
  const editorState = getViewData(EDITOR_VIEW_NAME);
  const stepPopperState = getComponentData(STEP_POPPER);

  const hasSteps = editorState?.project?.steps?.length > 0;
  if (!hasSteps) {
    return;
  }

  const step = getStep(stepId);  
  if (step === stepPopperState.currentStep || step === null) {
    return;
  }

  const newPopperState = { ...stepPopperState };
  newPopperState.currentStep = step;
  newPopperState.show = !isResponsiveView();
  let defaultDelay = 600;
  if (step.delay && step.delay > 0) {
    defaultDelay = step.delay;
  }  
  setTimeout(() => {
    setComponentData(STEP_POPPER, newPopperState);      
    requestAceControl(newPopperState.currentStep.ace);
    if (newPopperState.currentStep.selector === "#popperHelper") {
      //now we have to set absolute positioning for StepPopper over a hidden PopperHelper-Element
      setPopperHelper(newPopperState.currentStep);
    }
  }, defaultDelay)  
}

export const getStep = (stepId? : number) : any =>  {
  const editorState = getViewData(EDITOR_VIEW_NAME);
  const stepPopperState = getComponentData(STEP_POPPER);

  let stepIndex = 0;
  if (stepId === undefined) {
    if (stepPopperState.currentStep === undefined || stepPopperState.currentStep === null) {
      if (!editorState.project.steps) return null
      return editorState.project?.steps[0];
    }
  } else { // eslint-disable-line no-else-return
    editorState.project.steps.forEach((step : any, index : number) => {
      if (step.id === stepId) {
        stepIndex = index;
      }
    });

    return editorState.project.steps[stepIndex];
  }

  return null;
}

export const StepPopper = (props: { steps : any }) => {
  const [stepPopperState, setStepPopperState] = store.useState(STEP_POPPER);  

  useEffect(() => {
    if (stepPopperState.dismissed) return;
    if (!stepPopperState.currentStep && !stepPopperState.show) {
      showStep();
    }    
  }, [stepPopperState])

  const dismissSteps = () => {
    const newState = { ...stepPopperState }
    newState.dismissed = true;
    setStepPopperState(newState);
  }

  if (props.steps?.length > 0) {
    const stepIndex = stepPopperState?.currentStep?.id;
    if (!stepIndex) return null;
    const stepContent = props.steps[stepIndex - 1];

    //TODO: add logic for title and close button
    const tippyTarget = document.querySelector(stepContent?.selector);
    const tippyContent = (
      <div>
        <div className="text-end"><CloseButton onClick={dismissSteps} /></div>
        <div dangerouslySetInnerHTML={{__html: stepContent?.content}} />
    </div>);

    if (!tippyTarget) return null;

    const offsetSkidding = stepContent?.offsetSkidding ? stepContent?.offsetSkidding : 0;
    const offsetDistance = stepContent?.offsetDistance ? stepContent?.offsetDistance : 0;

    const theme = isDarkMode() ? 'material' : 'light-border';
    
    const tippy = <Draggable handle=".draggable-header">
      <Tippy content={tippyContent} visible={!stepPopperState.dismissed && stepPopperState.show} offset={[offsetSkidding,offsetDistance]} theme={theme} placement={stepContent?.placement} reference={tippyTarget} interactive={true} zIndex={1001} />
    </Draggable>;
    return tippy;
  }
  return null;
}
